import { Fragment } from 'react';
import type { BoolExpr, BoolExprGroup } from '@/proto/logiblocks/logiblocks';
import type {
  ConditionGroupReport,
  ConditionReport,
} from '@/proto/logiblocks/node_run_report';
import { LOGICAL_OP_LABELS } from '../../enumData';
import {
  DateTimeSetConditionRow,
  formatOutcome,
} from '../../runHistory/ConditionalAttemptDetail';
import { RunStepDetailField } from '../../runHistory/RunStepDetailField';
import type { ReactFlowNode, RunStepDetailProps } from '../../types';
import { formatVariableValue } from '../../variables/variableRefs';
import { formatComparedValues, formatLeaf } from './conditionalNodeUtils';
import styles from './ConditionalRunStepDetail.module.css';

/** Shows one condition: what it checked, the values it read, and its result. */
function ConditionDetail({
  expr,
  conditionReport,
  runGraphNodes,
}: {
  expr: BoolExpr;
  conditionReport: ConditionReport;
  runGraphNodes: ReactFlowNode[];
}) {
  // A condition that wasn't evaluated has no detail in its report, so its result is all it shows.
  const result = formatOutcome(conditionReport.outcome) ?? 'Not checked';
  if (expr.group !== undefined) {
    return (
      <RunStepDetailField label='Group'>
        {result}
        {conditionReport.group !== undefined && (
          <div className={styles.nestedGroup}>
            <ConditionGroupDetail
              group={expr.group}
              groupReport={conditionReport.group}
              runGraphNodes={runGraphNodes}
            />
          </div>
        )}
      </RunStepDetailField>
    );
  }
  if (
    expr.date_time_set_expr !== undefined &&
    conditionReport.date_time_set !== undefined
  ) {
    return (
      <DateTimeSetConditionRow
        condition={{
          expr: expr.date_time_set_expr,
          conditionReport,
          dateTimeSetReport: conditionReport.date_time_set,
        }}
      />
    );
  }
  const comparedValues = formatComparedValues(expr, conditionReport.comparison);
  return (
    <RunStepDetailField label={formatLeaf(expr, runGraphNodes)}>
      {conditionReport.variable_value !== undefined && (
        <div>{`Value: ${formatVariableValue(conditionReport.variable_value)}`}</div>
      )}
      {comparedValues !== null && (
        <div>{`Compared with: ${comparedValues}`}</div>
      )}
      <div>{result}</div>
    </RunStepDetailField>
  );
}

/**
 * Shows a group's conditions with the connectives between them. `groupReport.conditions[i]` reports
 * on `group.expressions[i]`.
 */
function ConditionGroupDetail({
  group,
  groupReport,
  runGraphNodes,
}: {
  group: BoolExprGroup;
  groupReport: ConditionGroupReport;
  runGraphNodes: ReactFlowNode[];
}) {
  // A report of another length can't be paired with the group's expressions.
  if (groupReport.conditions.length !== group.expressions.length) {
    console.error(
      `Conditional report has ${groupReport.conditions.length} conditions for a group of ` +
        `${group.expressions.length} expressions`
    );
    return <div>These conditions can&apos;t be shown.</div>;
  }
  return (
    <div className={styles.conditionList}>
      {group.expressions.map((expr, index) => (
        // eslint-disable-next-line react/no-array-index-key -- conditions have no ids; a report lists them in graph order and never reorders them
        <Fragment key={index}>
          {index > 0 && (
            <div className={styles.connective}>
              {LOGICAL_OP_LABELS[group.operators[index - 1]]}
            </div>
          )}
          <ConditionDetail
            expr={expr}
            conditionReport={groupReport.conditions[index]}
            runGraphNodes={runGraphNodes}
          />
        </Fragment>
      ))}
    </div>
  );
}

/** Shows each condition of a Conditional's step, from its graph data and its report. */
export function ConditionalRunStepDetail({
  step,
  runGraphNodes,
}: RunStepDetailProps) {
  const report = step.report?.conditional;
  if (report === undefined) {
    return null;
  }
  const root = step.nodeData?.conditional?.root;
  if (root === undefined || report.root === undefined) {
    console.error('Conditional report or its graph data has no root group');
    return null;
  }
  if (root.expressions.length === 0) {
    return <div>This path has no conditions.</div>;
  }
  return (
    <ConditionGroupDetail
      group={root}
      groupReport={report.root}
      runGraphNodes={runGraphNodes}
    />
  );
}
